class JudgemeProductReviews extends HTMLElement {
  constructor() {
    super();

    this.rendered = false;
    this.observer = null;
    this.pollTimer = null;
    this.timeouts = [];

    this.productId = this.dataset.productId;
    this.listEl = this.querySelector('[data-judgeme-pr-list]');
    this.loadMoreEl = this.querySelector('[data-judgeme-pr-load-more]');
    this.emptyEl = this.querySelector('[data-judgeme-pr-empty]');

    this.pageSize = Math.max(1, parseInt(this.dataset.pageSize || '5', 10));
    this.hasRating = this.dataset.hasRating === 'true';
    this.demoMode = this.dataset.demoMode === 'true';
    this.visibleCount = 0;
    this.items = [];

    this.texts = {
      anonymous: this.dataset.textAnonymous || 'Cliente',
      ratingTemplate: this.dataset.ariaRatingTemplate || 'Rating __RATING__ out of __MAX__ stars',
    };

    this.onLoadMore = this.onLoadMore.bind(this);
    this.tryHydrate = this.tryHydrate.bind(this);
  }

  connectedCallback() {
    if (!this.listEl) return;
    if (this.loadMoreEl) this.loadMoreEl.addEventListener('click', this.onLoadMore);

    if (this.demoMode) {
      this.initDemoPagination();
      return;
    }

    this.bindHydrationEvents();
    requestAnimationFrame(this.tryHydrate);

    [400, 1000, 2000, 4000, 7000].forEach((delay) => {
      this.timeouts.push(setTimeout(this.tryHydrate, delay));
    });

    this.timeouts.push(setTimeout(() => {
      if (!this.rendered) this.handleEmpty();
    }, 12000));
  }

  disconnectedCallback() {
    this.cleanup();
    if (this.loadMoreEl) this.loadMoreEl.removeEventListener('click', this.onLoadMore);
  }

  initDemoPagination() {
    this.items = Array.from(this.listEl.querySelectorAll('[data-judgeme-pr-item]'));

    if (!this.items.length) {
      this.handleEmpty();
      return;
    }

    this.items.forEach((item) => { item.setAttribute('hidden', ''); });
    this.visibleCount = 0;
    this.dataset.state = 'demo';
    this.revealNext();
  }

  revealNext() {
    const end = Math.min(this.visibleCount + this.pageSize, this.items.length);
    for (let i = this.visibleCount; i < end; i++) {
      this.items[i].removeAttribute('hidden');
    }
    this.visibleCount = end;
    this.updateLoadMoreVisibility(this.items.length);
  }

  bindHydrationEvents() {
    this.onJdgmUpdate = this.tryHydrate;
    document.addEventListener('jdgm.afterUpdate', this.onJdgmUpdate);
    document.addEventListener('jdgm:loaded', this.onJdgmUpdate);

    this.observer = new MutationObserver(this.tryHydrate);
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.onJdgmUpdate) {
      document.removeEventListener('jdgm.afterUpdate', this.onJdgmUpdate);
      document.removeEventListener('jdgm:loaded', this.onJdgmUpdate);
      this.onJdgmUpdate = null;
    }
    this.timeouts.forEach(clearTimeout);
    this.timeouts = [];
  }

  tryHydrate() {
    if (this.rendered) return;

    const reviews = this.extractReviews();
    if (!reviews.length) return;

    this.rendered = true;
    this.cleanup();
    this.renderReviews(reviews);
  }

  extractReviews() {
    const fromData = this.extractFromJdgmData();
    if (fromData.length) return fromData;

    const fromRevampDom = this.extractFromRevampDom();
    if (fromRevampDom.length) return fromRevampDom;

    return this.extractFromLegacyDom();
  }

  extractFromJdgmData() {
    const widget = this.getJdgmWidgetData();
    if (!widget || !Array.isArray(widget.reviews)) return [];

    return widget.reviews
      .map((r) => ({
        author: this.cleanText(r.reviewer_name) || this.texts.anonymous,
        title: this.cleanText(r.title),
        body: this.cleanText(r.body),
        rating: Math.max(0, Math.min(5, parseInt(r.rating, 10) || 0)),
        verified: !!r.verified_buyer,
      }))
      .filter((r) => r.body);
  }

  getJdgmWidgetData() {
    const jdgm = window.jdgm;
    if (!jdgm || !jdgm.data || !jdgm.data.reviewWidget || !this.productId) return null;
    return jdgm.data.reviewWidget[this.productId] || jdgm.data.reviewWidget[Number(this.productId)] || null;
  }

  extractFromRevampDom() {
    const nodes = document.querySelectorAll('.jdgm-review-widget .jm-review-item, .jm-review-widget .jm-review-item');
    if (!nodes.length) return [];

    return Array.from(nodes)
      .map((node) => {
        const nameEl = node.querySelector('.jm-reviewer-info__name');
        const bodyEl = node.querySelector('.jdgm-review-content__body-content, .jm-review-content__body');
        const ratingEl = node.querySelector('.jm-star-rating');

        let rating = 0;
        if (ratingEl) {
          const ariaLabel = ratingEl.getAttribute('aria-label') || '';
          const match = ariaLabel.match(/(\d+(?:\.\d+)?)/);
          if (match) rating = Math.round(parseFloat(match[1]));
        }

        return {
          author: this.cleanText(nameEl && nameEl.textContent) || this.texts.anonymous,
          body: this.cleanText(bodyEl && bodyEl.textContent),
          rating: Math.max(0, Math.min(5, rating)),
        };
      })
      .filter((r) => r.body);
  }

  extractFromLegacyDom() {
    const reviewNodes = document.querySelectorAll('.jdgm-widget.jdgm-review-widget .jdgm-rev');
    if (!reviewNodes.length) return [];

    return Array.from(reviewNodes)
      .map((node) => this.parseLegacyReviewNode(node))
      .filter((r) => r.body);
  }

  parseLegacyReviewNode(node) {
    const authorEl = node.querySelector('.jdgm-rev__author');
    const bodyEl = node.querySelector('.jdgm-rev__body') || node.querySelector('.jdgm-rev__content');
    const titleEl = node.querySelector('.jdgm-rev__title');

    let rating = 0;
    const ratingEl = node.querySelector('[data-score]') || node.querySelector('.jdgm-rev__rating');
    if (ratingEl) {
      const score = ratingEl.dataset.score || ratingEl.getAttribute('data-score');
      if (score) rating = parseInt(score, 10);
    }
    if (!rating) {
      rating = node.querySelectorAll('.jdgm-star.jdgm--on').length;
    }
    if (!rating) {
      const ariaLabel = ratingEl ? ratingEl.getAttribute('aria-label') : '';
      const match = ariaLabel && ariaLabel.match(/(\d+)/);
      if (match) rating = parseInt(match[1], 10);
    }

    return {
      author: this.cleanText(authorEl && authorEl.textContent) || this.texts.anonymous,
      title: this.cleanText(titleEl && titleEl.textContent).slice(0, 140),
      body: this.cleanText(bodyEl && bodyEl.textContent),
      rating: Math.max(0, Math.min(5, rating || 0)),
    };
  }

  cleanText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  renderReviews(reviews) {
    this.listEl.removeAttribute('hidden');
    this.listEl.innerHTML = '';
    this.items = [];
    this.visibleCount = 0;

    const fragment = document.createDocumentFragment();
    reviews.forEach((review) => {
      const li = document.createElement('li');
      li.className = 'judgeme-pr__item';
      li.setAttribute('data-judgeme-pr-item', '');
      li.setAttribute('hidden', '');
      li.innerHTML = this.reviewHtml(review);
      fragment.appendChild(li);
      this.items.push(li);
    });
    this.listEl.appendChild(fragment);

    this.dataset.state = 'loaded';
    this.revealNext();
    this.updateRingFromData();
  }

  /**
   * Re-pinta el anillo de progreso con el rating del JSON oficial de Judge.me.
   *
   * El cálculo server-side (Liquid) depende de
   * `product.metafields.reviews.rating.value.scale_max`, que Judge.me no
   * siempre rellena (o sincroniza con retraso). En ese caso `rating_percent`
   * se queda en 0 y el arco no se dibuja. El JSON del widget oficial sí
   * trae el `average_rating` real, así que es la fuente de verdad.
   */
  updateRingFromData() {
    const widget = this.getJdgmWidgetData();
    if (!widget) return;

    const rating = parseFloat(widget.average_rating);
    if (!isFinite(rating) || rating <= 0) return;

    const scale = 5;
    const display = rating.toFixed(1);
    const percent = Math.max(0, Math.min(100, (rating / scale) * 100));
    const circumference = 314.159;
    const offset = (circumference * (100 - percent) / 100).toFixed(3);

    const progressEl = this.querySelector('.judgeme-pr__ring-progress');
    if (progressEl) {
      progressEl.setAttribute('stroke-dashoffset', offset);
    }

    const scoreEl = this.querySelector('.judgeme-pr__ring-score');
    if (scoreEl) scoreEl.textContent = display;

    const ringEl = this.querySelector('.judgeme-pr__ring');
    if (ringEl) {
      const ariaLabel = (this.dataset.ariaRatingTemplate || '')
        .replace('__RATING__', display)
        .replace('__MAX__', String(scale));
      if (ariaLabel) ringEl.setAttribute('aria-label', ariaLabel);
    }
  }

  reviewHtml(review) {
    const author = this.escape(review.author || this.texts.anonymous);
    const title = review.title
      ? `<h3 class="judgeme-pr__item-title">${this.escape(review.title)}</h3>`
      : '';
    const body = this.escape(review.body).replace(/\n/g, '<br>');
    const ratingAria = this.escape(
      this.texts.ratingTemplate.replace('__RATING__', String(review.rating)).replace('__MAX__', '5')
    );

    return `
      <div class="judgeme-pr__item-rating" role="img" aria-label="${ratingAria}">
        <span class="judgeme-pr__stars" style="--rating: ${review.rating};" aria-hidden="true"></span>
      </div>
      ${title}
      <p class="judgeme-pr__item-author">${author}</p>
      <p class="judgeme-pr__item-body">${body}</p>
    `;
  }

  onLoadMore(event) {
    event.preventDefault();
    this.revealNext();
  }

  updateLoadMoreVisibility(total) {
    if (!this.loadMoreEl) return;
    if (this.visibleCount >= total) {
      this.loadMoreEl.setAttribute('hidden', '');
    } else {
      this.loadMoreEl.removeAttribute('hidden');
    }
  }

  handleEmpty() {
    this.cleanup();
    this.rendered = true;

    if (this.listEl) this.listEl.setAttribute('hidden', '');
    if (this.loadMoreEl) this.loadMoreEl.setAttribute('hidden', '');

    if (!this.hasRating) {
      if (!window.Shopify || !window.Shopify.designMode) {
        this.setAttribute('hidden', '');
        this.dataset.state = 'hidden';
        return;
      }
      this.dataset.state = 'empty';
      if (this.emptyEl) this.emptyEl.removeAttribute('hidden');
    } else {
      this.dataset.state = 'rating-only';
    }
  }

  escape(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('judgeme-product-reviews')) {
  customElements.define('judgeme-product-reviews', JudgemeProductReviews);
}
